import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Theme, themes, defaultTheme, getTheme } from './themes';

interface ThemeContextType {
  theme: Theme;
  themeId: string;
  setTheme: (themeId: string) => void;
  availableThemes: Array<{ id: string; name: string; description: string }>;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = 'mojos-class-theme';

interface ThemeProviderProps {
  children: ReactNode;
  defaultThemeId?: string;
}

export function ThemeProvider({ children, defaultThemeId }: ThemeProviderProps) {
  const [themeId, setThemeId] = useState<string>(() => {
    // Try to get from localStorage, fallback to default
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (stored && themes[stored]) {
        return stored;
      }
    }
    return defaultThemeId || defaultTheme.id;
  });

  const theme = getTheme(themeId);

  // Apply theme as CSS variables
  useEffect(() => {
    const root = document.documentElement;

    // Base colors
    root.style.setProperty('--bg', theme.background);
    root.style.setProperty('--bg-alt', theme.backgroundAlt);
    root.style.setProperty('--surface', theme.surface);

    // Brand gradient
    root.style.setProperty('--grad-start', theme.gradientStart);
    root.style.setProperty('--grad-end', theme.gradientEnd);

    // CTA colors
    root.style.setProperty('--cta', theme.cta);
    root.style.setProperty('--cta-dk', theme.ctaDark);
    root.style.setProperty('--cta-lt', theme.ctaLight);

    // Accent/Amber colors
    root.style.setProperty('--amber', theme.amber);
    root.style.setProperty('--amber-lt', theme.amberLight);
    root.style.setProperty('--amber-dk', theme.amberDark);

    // Functional colors
    root.style.setProperty('--green', theme.success);
    root.style.setProperty('--green-lt', theme.successLight);
    root.style.setProperty('--red', theme.error);
    root.style.setProperty('--red-lt', theme.errorLight);

    // Text colors
    root.style.setProperty('--text', theme.text);
    root.style.setProperty('--muted', theme.textMuted);
    root.style.setProperty('--light', theme.textLight);
    root.style.setProperty('--on-brand', theme.textOnBrand);

    // Border & shadow
    root.style.setProperty('--border', theme.border);
    root.style.setProperty('--shadow', theme.shadow);
    root.style.setProperty('--shadow-md', theme.shadowMd);

    // Sidebar
    root.style.setProperty('--sidebar-bg', theme.sidebarBg);
    root.style.setProperty('--sidebar-text', theme.sidebarText);
    root.style.setProperty('--sidebar-text-muted', theme.sidebarTextMuted);
    root.style.setProperty('--sidebar-active', theme.sidebarActive);
    root.style.setProperty('--sidebar-active-bg', theme.sidebarActiveBg);

    // Legacy mappings for compatibility
    root.style.setProperty('--color-background', theme.background);
    root.style.setProperty('--color-background-alt', theme.backgroundAlt);
    root.style.setProperty('--color-surface', theme.surface);
    root.style.setProperty('--color-surface-hover', theme.surfaceHover);
    root.style.setProperty('--color-surface-pressed', theme.surfacePressed);
    root.style.setProperty('--color-primary', theme.primary);
    root.style.setProperty('--color-primary-light', theme.primaryLight);
    root.style.setProperty('--color-primary-dark', theme.primaryDark);
    root.style.setProperty('--color-secondary', theme.secondary);
    root.style.setProperty('--color-secondary-light', theme.secondaryLight);
    root.style.setProperty('--color-accent', theme.accent);
    root.style.setProperty('--color-accent-light', theme.accentLight);
    root.style.setProperty('--color-text', theme.text);
    root.style.setProperty('--color-text-muted', theme.textMuted);
    root.style.setProperty('--color-text-light', theme.textLight);
    root.style.setProperty('--color-text-on-primary', theme.textOnPrimary);
    root.style.setProperty('--color-success', theme.success);
    root.style.setProperty('--color-success-light', theme.successLight);
    root.style.setProperty('--color-warning', theme.warning);
    root.style.setProperty('--color-warning-light', theme.warningLight);
    root.style.setProperty('--color-error', theme.error);
    root.style.setProperty('--color-error-light', theme.errorLight);
    root.style.setProperty('--color-info', theme.info);
    root.style.setProperty('--color-info-light', theme.infoLight);
    root.style.setProperty('--color-border', theme.border);
    root.style.setProperty('--color-border-light', theme.borderLight);

    // Short aliases — several customer components reference these names.
    // Without them the values compute to invalid/transparent, silently
    // breaking badge fills, progress tracks and muted text.
    root.style.setProperty('--color-bg', theme.background);
    root.style.setProperty('--color-bg-2', theme.backgroundAlt);
    root.style.setProperty('--color-muted', theme.textMuted);
    root.style.setProperty('--color-light', theme.textLight);

    // Update body
    document.body.style.backgroundColor = theme.background;
    document.body.style.color = theme.text;

    // Persist to localStorage
    localStorage.setItem(THEME_STORAGE_KEY, themeId);
  }, [theme, themeId]);

  const handleSetTheme = (newThemeId: string) => {
    if (themes[newThemeId]) {
      setThemeId(newThemeId);
    }
  };

  const availableThemes = Object.values(themes).map(({ id, name, description }) => ({
    id,
    name,
    description,
  }));

  return (
    <ThemeContext.Provider
      value={{
        theme,
        themeId,
        setTheme: handleSetTheme,
        availableThemes,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

// Hook to get current theme colors for use in JS (e.g., charts)
export function useThemeColors() {
  const { theme } = useTheme();
  return theme;
}
